// Typed client for the racing feed endpoints (Next To Jump / Results / Futures).
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { authHeader } from './auth';

export interface Favourite {
  runner_name: string;
  selection_id: number;
  price: number;
}

export interface RaceCard {
  market_id: string;
  meeting: string;
  event_name: string;
  market_name?: string;
  race_number: number;
  start_time: string;
  date: string;
  status: string;
  in_play: boolean;
  total_matched: number;
  runner_count: number;
  favourite: Favourite | null;
}

export interface ResultCard extends RaceCard {
  // True once Betfair settlement is captured (CLOSED / WINNER tagged).
  settled: boolean;
  winner: {
    runner_name: string;
    selection_id: number;
    bsp: number | null;
    last_traded: number | null;
    result_price: number | null;
  } | null;
  placed: { runner_name: string; status: string; bsp: number | null; last_traded: number | null }[];
}

export interface FutureMeeting {
  meeting: string;
  date: string;
  race_count: number;
  first_jump: string;
  last_jump: string;
  total_matched: number;
}

export interface FutureDay {
  date: string;
  meetings: FutureMeeting[];
}

export const getNextToJump = async (limit = 30): Promise<RaceCard[]> =>
  (await axios.get(`${API_BASE_URL}/api/next-to-jump?limit=${limit}`)).data;

export const getResults = async (limit = 50): Promise<ResultCard[]> =>
  (await axios.get(`${API_BASE_URL}/api/results?limit=${limit}`)).data;

export const getFutures = async (): Promise<FutureDay[]> =>
  (await axios.get(`${API_BASE_URL}/api/futures`)).data;

// Day Results — every race for a date, grouped by meeting, with Win/Place BSP.
export interface DayResultRunner { runner_name: string; selection_id: number | null; win_bsp: number | null; place_bsp: number | null; jockey?: string | null; trainer?: string | null; barrier?: number | null; }
export interface DayResultRace {
  market_id: string; meeting: string; race_number: number; race_name: string | null; start_time: string | null;
  surface?: string | null;
  settled: boolean; win_volume: number; place_volume: number | null;
  winner: DayResultRunner | null; placed: DayResultRunner[]; order_approx: boolean;
  date?: string; // present in the "All" (cross-date) view
}
export interface DayResultMeeting { meeting: string; races: DayResultRace[]; }
export interface DayResults { date: string; meetings: DayResultMeeting[]; total_races: number; settled_races: number; }
export const getDayResults = async (date?: string): Promise<DayResults> =>
  (await axios.get(`${API_BASE_URL}/api/results/day${date ? `?date=${date}` : ''}`)).data;

// All Results — flat, newest-first, paginated (the "All" button). 1000 races per page.
export interface AllResults {
  all: true; page: number; limit: number; total_races: number; total_pages: number;
  returned: number; settled_races: number; races: DayResultRace[];
}
export const getAllResults = async (page = 0, limit = 1000): Promise<AllResults> =>
  (await axios.get(`${API_BASE_URL}/api/results/all?page=${page}&limit=${limit}`)).data;

// Staking Brain — Paper Mode. Read-only, zero real money — every row is a hypothetical
// decision from the client's real trained model, graded against the real settled result.
// Field shape corrected 2026-09-10 to match what staking_brain_paper.py actually writes as of
// the 2026-09-09 merit-gate migration (the old p_merit/p_brain/sigma_total/kelly_full/role/
// funding_tier/brain_target_stake/selection_decision/funding_status fields no longer exist on
// any row scored since then).
export interface StakingBrainPriceSnapshot {
  earliest_snapshot_price: number | null; earliest_snapshot_minutes_to_jump: number | null;
  latest_snapshot_price: number | null; latest_snapshot_minutes_to_jump: number | null;
  price_drift_pct: number | null; snapshot_count: number;
}
export interface StakingBrainPaperRow extends StakingBrainPriceSnapshot {
  stream_id?: string; stream_name?: string; off_at?: string; benchmark_pnl_units?: number|null;
  date: string; track: string; race: string; runner: string;
  engine_source: string | null; bet_type: 'WIN' | 'PLACE'; model_version: string;
  barrier: number | null; weight: number | null; jockey: string | null; trainer: string | null;
  decision_price: number | null; core_rank: number | null; market_rank: number | null;
  core_reliability: number | null; raw_model_probability: number | null; model_fair_price: number | null;
  selection_state: string | null; break_even_probability: number | null; expected_unit_return: number | null;
  applied_stake: number | null; stake_role: string | null;
  conviction_tier: 'BASE' | 'CONFIDENT' | 'SLAM_DUNK' | null;
  is_live_merit_candidate: boolean;
  merit_total_score: number | null; merit_dimension_scores: Record<string, number> | null;
  merit_evidence_reasons: string[]; merit_data_limitations: string[]; merit_robustness: string | null;
  authoritative_decision_source: string | null;
  decision_reasons: string[];
  safety_blocked: boolean; safety_reasons: string[]; health_alerts: string[];
  legacy_ev_diagnostic: Record<string, any> | null;
  decision: 'BET' | 'PASS' | 'PROVISIONAL' | 'NOT_EVALUATED_NOT_RANK1_OR_3'; status: 'PENDING' | 'SETTLED' | 'PROVISIONAL' | 'PASS';
  result: string | null; paper_pnl_dollars: number | null;
  build_id: string | null; architecture_version: string | null; core_model_version: string | null;
  config_hash: string | null; scored_at: string | null;
}
export interface StakingBrainProvenance {
  build_id: string | null; architecture_version: string | null; core_model_version: string | null;
  config_hash: string | null; model_version: string | null;
}
export interface StakingBrainPaper {
  date: string | null; from: string | null; to: string | null;
  view: 'bets' | 'all'; active_model_version: string;
  opening_bank: number; current_bank: number;
  total_rows: number; pending: number; settled: number;
  bets: number; wins: number; strike_rate: number | null;
  pnl_dollars: number; roi_pct: number | null;
  provenance: StakingBrainProvenance;
  rows: StakingBrainPaperRow[];
}
export const getStakingBrainPaper = async (opts?: { date?: string; from?: string; to?: string; view?: 'bets' | 'all'; scope?: 'active' }): Promise<StakingBrainPaper> => {
  const params = new URLSearchParams();
  if (opts?.date) params.set('date', opts.date);
  if (opts?.from) params.set('from', opts.from);
  if (opts?.to) params.set('to', opts.to);
  if (opts?.view) params.set('view', opts.view);
  if (opts?.scope) params.set('scope', opts.scope);
  const qs = params.toString();
  return (await axios.get(`${API_BASE_URL}/api/strategy/staking-brain-paper${qs ? `?${qs}` : ''}`, { headers: authHeader() })).data;
};

export const downloadStakingBrainCsv = async (opts?: { date?: string; from?: string; to?: string }): Promise<void> => {
  const params = new URLSearchParams();
  if (opts?.date) params.set('date', opts.date);
  if (opts?.from) params.set('from', opts.from);
  if (opts?.to) params.set('to', opts.to);
  params.set('format', 'csv');
  params.set('scope', 'active');
  const res = await fetch(`${API_BASE_URL}/api/strategy/staking-brain-paper?${params.toString()}`, { headers: { ...authHeader() } });
  if (!res.ok) throw new Error(`CSV export failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const label = opts?.from || opts?.to ? `${opts?.from || 'start'}_to_${opts?.to || 'now'}` : (opts?.date || 'all');
  a.href = url; a.download = `betbuilder-staking-brain-${label}.csv`;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
};

export interface StakingBrainStreamEvaluation {
  priced_bets?: number; missing_price?: number; from?: string; to?: string; monthly?: Array<{month:string;selected:number;settled:number;wins:number;roi_pct:number|null;pnl_units:number}>;
  evidence_period: string; year?: number; selected?: number; settled?: number; wins?: number;
  strike_rate_pct?: number | null; roi_pct?: number | null; pnl_units?: number | null;
  evidence_status?: string; source_run_id?: string;
  roi_without_top3_wins_pct?: number | null; max_drawdown_units?: number | null;
  recommendation?: string; evaluated_at?: string;
}

export interface StakingBrainStream {
  page_qualification?: { qualifies: boolean; trained_years: number[]; roi_threshold_pct: number };
  stream_id: string; version: string; name: string;
  state: 'RESEARCH' | 'ACTIVE_PAPER' | 'PROMOTION_CANDIDATE' | 'RETIRED' | 'REJECTED';
  origin: 'OWNER' | 'EXTERNAL_SOURCE' | 'AUTONOMOUS_RESEARCH';
  rank_universe: number[]; rank_scope_label?: string; evaluation_limitations?: string[]; hypothesis: string; spec_hash: string;
  paper_only: boolean; live_authority: boolean; created_at?: string;
  rule_spec: Record<string, any>;
  execution_price_policy: { minimum: number | null; maximum: number | null; entry_strategy: string; predicted_movement: string };
  research_dossier?: { objective: string; mechanism: string; why_created: string;
    selection_rule: string; execution_rule: string; evidence_required: string; failure_rule: string };
  suspension_reason?: string; suspended_at?: string;
  evaluations: StakingBrainStreamEvaluation[];
}

export interface StakingBrainResearchTheory {
  theory_id: string; hypothesis: string; source: string; source_url?: string;
  state: string; required_inputs?: string[]; candidate_ceilings?: number[];
  finding?: string; result_summary?: string; registered_at?: string;
  frozen_rule?: Record<string, any>; backtest_method?: string;
  evaluations?: Array<{ evidence_period: string; year?: number; maximum_price?: number | null;
    bets?: number; wins?: number; strike_rate_pct?: number | null; roi_pct?: number | null;
    pnl_units?: number | null; roi_without_top3_wins_pct?: number | null;
    max_drawdown_units?: number | null }>;
}

export interface StakingBrainStreamsResponse {
  trained_worker_health?: {updated_at:string;active_streams:number;new_decisions:number;skipped:Record<string,number>};
  generated_at: string; live_authority: false; current_control: string;
  multiple_testing_policy: string;
  research: { engine_version: string; trial_count: number; theory_count: number;
    paper_decision_count: number; grid_version: string | null; grid_frozen_at: string | null;
    theories: StakingBrainResearchTheory[] };
  streams: StakingBrainStream[];
  external_sources: Record<string, { access: string; visibility: string }>;
  reasoning_categories: string[];
}

export const getStakingBrainStreams = async (): Promise<StakingBrainStreamsResponse> =>
  (await axios.get(`${API_BASE_URL}/api/strategy/staking-brain-streams`, {
    headers: authHeader(),
  })).data;

// Shared helpers ─────────────────────────────────────────────────────────────

// Build the /race link used across pages (matches Dashboard's format).
// `ref` records which feed we came from so the race page's back button can
// return there (e.g. 'results' → Back to Results).
export const raceLink = (meeting: string, raceNumber: number, date?: string, ref?: string) => {
  const params = new URLSearchParams();
  if (date) params.set('date', date);
  if (ref) params.set('ref', ref);
  const qs = params.toString();
  return `/race/${encodeURIComponent(meeting)}/${raceNumber}${qs ? `?${qs}` : ''}`;
};

// Time-to-jump label + severity class, relative to `nowMs`.
export const timeToJump = (startTime: string, nowMs: number) => {
  const diff = new Date(startTime).getTime() - nowMs;
  if (diff <= -120000) return { label: 'Off', cls: 'off' as const };
  if (diff <= 0) return { label: 'Jumped', cls: 'jumped' as const };
  const totalMins = Math.floor(diff / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  if (totalMins >= 60) {
    const h = Math.floor(totalMins / 60);
    return { label: `${h}h ${totalMins % 60}m`, cls: 'far' as const };
  }
  if (totalMins >= 1) {
    return {
      label: `${totalMins}m ${secs.toString().padStart(2, '0')}s`,
      cls: totalMins < 5 ? ('soon' as const) : ('normal' as const),
    };
  }
  return { label: `${secs}s`, cls: 'soon' as const };
};

// Australian racing times are shown in AEST/AEDT (eastern), not the viewer's
// browser timezone. Betfair feeds give UTC instants, so rendering them in the
// browser zone (e.g. IST) showed the wrong clock. Covers NSW/VIC/QLD/ACT/TAS
// exactly; WA/SA differ by their offset (no per-track zone data available).
export const AU_TZ = 'Australia/Sydney';

// True when the string carries an explicit UTC 'Z' or numeric offset — a real
// instant we must convert to AU time. Naive strings like "6/6/2026 4:15:00 PM"
// (PuntingForm) are already the track's local wall-clock and must NOT be shifted.
const hasExplicitZone = (value: string) => /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(value.trim());

export const fmtTime = (value: string) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
  if (hasExplicitZone(value)) opts.timeZone = AU_TZ;
  return d.toLocaleTimeString('en-AU', opts);
};

export const fmtDateTime = (value: string) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const opts: Intl.DateTimeFormatOptions = {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  };
  if (hasExplicitZone(value)) opts.timeZone = AU_TZ;
  return d.toLocaleString('en-AU', opts);
};

export const fmtDateLong = (dateStr: string) => {
  const d = new Date(`${dateStr}T00:00:00`);
  return d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });
};

export const getWinningBrainStreams = async (): Promise<{generated_at:string;streams:StakingBrainStream[]}> =>
  (await axios.get(`${API_BASE_URL}/api/strategy/staking-brain-streams?winning=1`, {
    headers: authHeader(),
  })).data;

export interface BrainUpcoming {
 generated_at: string|null; until?: string; next_refresh_at?: string; checked_races?: number;
 rows: {race_id:string;off_at:string;track:string;race:string;runner:string;brain_prob:number;selection_status:string;daily_slot?:number}[];
}
export async function getBrainUpcoming():Promise<BrainUpcoming> {
 const {data}=await axios.get(API_BASE_URL+'/api/strategy/brain-upcoming',{headers:authHeader()});
 return data;
}
